import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './AriaLiveAnnouncer.css';

/**
 * AriaLiveAnnouncer - A visually hidden component that announces messages to screen readers
 * Uses aria-live="polite" to announce context changes without interrupting the user
 */
const AriaLiveAnnouncer = ({ message, timestamp }) => {
  const announceRef = useRef(null);

  useEffect(() => {
    // Clear and re-announce when message or timestamp changes
    // This ensures the same message can be announced multiple times
    if (message && announceRef.current) {
      // Briefly clear the content to ensure the screen reader picks up the change
      announceRef.current.textContent = '';

      // Use setTimeout to ensure the DOM update happens before setting new content
      setTimeout(() => {
        if (announceRef.current) {
          announceRef.current.textContent = message;
        }
      }, 10);
    }
  }, [message, timestamp]);

  return (
    <div
      ref={announceRef}
      className="sr-only"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    />
  );
};

AriaLiveAnnouncer.propTypes = {
  message: PropTypes.string,
  timestamp: PropTypes.number,
};

AriaLiveAnnouncer.defaultProps = {
  message: '',
  timestamp: null,
};

export default AriaLiveAnnouncer;
